/**
 * A one-value slot the current page can fill in the app header.
 *
 * Chat UX brief 2026-09-07 change #7 moves the thread's identity —
 * "Talking with <persona>" — out of the chat page's own second header row and
 * into the top row beside the back arrow. The Header must therefore render a
 * string that only the page knows.
 *
 * It is a context rather than a hook call inside `Header` on purpose. The
 * persona name comes from a react-query hook, and reaching for it in the
 * app-wide header would make every surface that renders `Header` — including
 * a dozen tests that render it bare — require a QueryClientProvider. The page
 * already has the resolved name; it publishes it here and clears it on the way
 * out.
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { HeaderTitleContext } from './header-title';

export function HeaderTitleProvider({ children }: { children: ReactNode }) {
  const [title, setTitleState] = useState<ReactNode | null>(null);
  const setTitle = useCallback((next: ReactNode | null) => setTitleState(next), []);
  const value = useMemo(() => ({ title, setTitle }), [title, setTitle]);
  return <HeaderTitleContext.Provider value={value}>{children}</HeaderTitleContext.Provider>;
}
